/**
 * SCM7 — itemized CAPEX feedback loop into `FinancialAssumptions`.
 *
 * Pure functions only — no UI here. The `<CapexVarianceTable>` in
 * `src/components/siterag/supplyChain/CapexVarianceTable.tsx` consumes
 * the structured output of `itemizedCapexFromBom` + `compareCapex`.
 *
 * Strategic frame: feasibility reports today carry a top-line CAPEX
 * pulled from the uploaded financial model. Once the SCM pack
 * derives a BoM with industry-baseline pricing per item, the user
 * gets two numbers — uploaded-model CAPEX vs BoM-priced CAPEX — and
 * a per-category delta. Variance > 10% trips an amber flag in the
 * UI so the user can investigate (different vendor pricing, different
 * scope, missing scope).
 *
 * TODO(orchestrator): SCM-Data will land the canonical
 * `src/types/supplyChain.ts`. The local stub in this branch matches
 * the spec shapes so a swap is mechanical at merge time.
 */
import type {
  BomItem,
  SCMCategory,
  SupplyChainPlan,
} from '../types/supplyChain';
import { selectActiveBomItems } from '../types/supplyChain';
import type { FieldProvenance } from '../types/financialAssumptions';

/**
 * Sum of priced BoM items, grouped by category. Items with
 * `totalCostUsd == null` are counted as `unpricedItemCount` and
 * excluded from the totals — they shouldn't suppress the category's
 * subtotal when most items in the category are priced.
 */
export interface ItemizedCapex {
  byCategory: Partial<Record<SCMCategory, number>>;
  total: number;
  unpricedItemCount: number;
  /** Number of priced items contributing to the total. */
  pricedItemCount: number;
}

/**
 * One row of the per-category variance table.
 * `modelUsd` is null when the user hasn't supplied an uploaded
 * financial model (then `deltaUsd` and `deltaPct` are also null).
 */
export interface CapexVarianceRow {
  category: SCMCategory | 'total';
  label: string;
  modelUsd: number | null;
  bomUsd: number;
  deltaUsd: number | null;
  deltaPct: number | null;
  /** True when |deltaPct| ≥ flagThresholdPct. */
  flagged: boolean;
}

export interface CapexVarianceTable {
  rows: CapexVarianceRow[];
  totalRow: CapexVarianceRow;
  unpricedItemCount: number;
  pricedItemCount: number;
}

export const DEFAULT_VARIANCE_FLAG_PCT = 10;

const CATEGORY_LABELS: Record<SCMCategory, string> = {
  'power-generation': 'Power generation',
  'power-conversion': 'Power conversion',
  'energy-storage': 'Energy storage',
  cooling: 'Cooling',
  'data-center-it': 'Data center IT',
  connectivity: 'Connectivity',
  'civil-construction': 'Civil & construction',
  logistics: 'Logistics',
  commissioning: 'Commissioning',
};

const CATEGORY_ORDER: SCMCategory[] = [
  'power-generation',
  'power-conversion',
  'energy-storage',
  'cooling',
  'data-center-it',
  'connectivity',
  'civil-construction',
  'logistics',
  'commissioning',
];

/**
 * Sum `bomItem.totalCostUsd` by category. Items with null totals are
 * counted but not added.
 *
 * BOM-ROLLUP: mutually-exclusive procurement ALTERNATIVES (lines sharing an
 * `optionGroupId`) are collapsed to the ONE active option per group (via
 * `selectActiveBomItems`) BEFORE summing — so three competing Phase-1 prime
 * movers @ $240M each contribute $240M, not their $720M sum. Legacy BoMs with
 * no option groups are unaffected (every line is additive).
 */
export function itemizedCapexFromBom(plan: SupplyChainPlan | null | undefined): ItemizedCapex {
  const byCategory: Partial<Record<SCMCategory, number>> = {};
  let unpriced = 0;
  let priced = 0;
  if (!plan || !Array.isArray(plan.bom)) {
    return { byCategory, total: 0, unpricedItemCount: 0, pricedItemCount: 0 };
  }
  for (const item of selectActiveBomItems(plan.bom)) {
    // Guard non-finite (NaN/Infinity) as well as null — a non-finite total
    // is "unpriced", never summed (otherwise ONE NaN line poisons the whole
    // total to NaN and the A4 conservation guard can't reconcile). Matches the
    // Number.isFinite discipline in computePerPhaseCapexFromBom.
    if (item.totalCostUsd == null || !Number.isFinite(item.totalCostUsd)) {
      unpriced += 1;
      continue;
    }
    priced += 1;
    byCategory[item.category] = (byCategory[item.category] ?? 0) + item.totalCostUsd;
  }
  const total = Object.values(byCategory).reduce<number>((acc, v) => acc + (v ?? 0), 0);
  return { byCategory, total, unpricedItemCount: unpriced, pricedItemCount: priced };
}

/**
 * Per-category variance vs an uploaded financial model.
 *
 * The model CAPEX is a single top-line number (`financial.capexTotalUsd`),
 * not itemized, so the model row only contributes to the **total**
 * variance — per-category model values are null until the FA pack
 * grows a richer breakdown surface. This keeps the function honest
 * about what we can compare today.
 *
 * `flagThresholdPct` is the |deltaPct| above which the row flips
 * `flagged: true` (default 10%, per spec).
 */
export function buildCapexVarianceTable(
  plan: SupplyChainPlan | null | undefined,
  financialCapexTotalUsd: number | null | undefined,
  options: { flagThresholdPct?: number } = {},
): CapexVarianceTable {
  const flagThreshold = options.flagThresholdPct ?? DEFAULT_VARIANCE_FLAG_PCT;
  const itemized = itemizedCapexFromBom(plan);

  const rows: CapexVarianceRow[] = CATEGORY_ORDER.flatMap((cat) => {
    const bomUsd = itemized.byCategory[cat];
    if (bomUsd == null || bomUsd === 0) return [];
    return [
      {
        category: cat,
        label: CATEGORY_LABELS[cat],
        modelUsd: null,
        bomUsd,
        deltaUsd: null,
        deltaPct: null,
        flagged: false,
      },
    ];
  });

  const modelTotal = sanitizeUsd(financialCapexTotalUsd);
  const bomTotal = itemized.total;
  const deltaUsd = modelTotal == null ? null : bomTotal - modelTotal;
  const deltaPct =
    modelTotal == null || modelTotal === 0
      ? null
      : (deltaUsd! / modelTotal) * 100;
  const totalRow: CapexVarianceRow = {
    category: 'total',
    label: 'Total CAPEX',
    modelUsd: modelTotal,
    bomUsd: bomTotal,
    deltaUsd,
    deltaPct,
    flagged: deltaPct != null && Math.abs(deltaPct) >= flagThreshold,
  };

  return {
    rows,
    totalRow,
    unpricedItemCount: itemized.unpricedItemCount,
    pricedItemCount: itemized.pricedItemCount,
  };
}

function sanitizeUsd(value: number | null | undefined): number | null {
  if (value == null) return null;
  if (!Number.isFinite(value)) return null;
  return value;
}

// ─────────────────────────────────────────────────────────────────────────────
// D5 — variance self-comparison guard (Wave D keystone).
//
// WHY: the variance table compares the "uploaded-model" CAPEX against the
// BoM-priced CAPEX. But when the model CAPEX was ITSELF derived from the BoM
// (provenance source 'derived', cellReference 'supplyChainPlan.bom' — written by
// applyCapexSyncToFinancialAssumptions / buildFinancialPullFromSources), the two
// columns are the SAME number and the delta is a meaningless 0% self-comparison
// that dresses up as a real reconciliation. This helper lets the (downstream)
// variance surface DETECT that case and suppress / re-label the comparison.
//
// WIRED (Phase 3.9, audit D20): `patchSedWithSupplyChainInsights` reads the
// model's `capexTotalUsd` provenance through this detector and, when it is
// BoM-derived, withholds the model figure from `buildCapexVarianceTable`, so
// `supplyChainSnapshot.capexVariancePct` is NULL and the insight summary states
// that the two figures are the same number — instead of publishing a "+0.0%
// variance". Every SED / prompt / report consumer inherits the suppression from
// there. Any NEW variance surface must go through this detector too.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The cellReference stamped by the supply-chain → finance CAPEX sync. Mirrors
 * `BOM_ROLLUP_CELL_REFERENCE` in `syncCapexFromSupplyChain.ts`; duplicated here
 * (rather than imported) to keep `itemizedCapex.ts` free of a store/sync
 * dependency. Kept in sync by the D5 test.
 */
export const BOM_DERIVED_CELL_REFERENCE = 'supplyChainPlan.bom';

/**
 * D5 — true when a `capexTotalUsd` field-provenance indicates the value was
 * DERIVED FROM THE BOM (so comparing it against the BoM-priced CAPEX is a
 * meaningless self-comparison). Detection rule: provenance source is 'derived'
 * AND its cellReference points at the supply-chain plan. Pure; null/undefined
 * provenance → false (a missing provenance is treated as a genuine, comparable
 * external value, never suppressed).
 */
export function isCapexProvenanceBomDerived(
  provenance: FieldProvenance | null | undefined,
): boolean {
  if (!provenance) return false;
  if (provenance.source !== 'derived') return false;
  return provenance.cellReference === BOM_DERIVED_CELL_REFERENCE;
}

export const __test__ = { CATEGORY_LABELS, CATEGORY_ORDER };
